import { MergeOrUpdateStateAction } from '@tableau/vizportalclient-common-components';

import { Messages } from '~src/codegen/Localize';
import { openReactActionDialog, ReactActionDialogOptions } from '~src/commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { CheckConnectionCredentialsAction } from '~src/connectivity/services/CheckConnectionCredentialsAction';
import { Flows } from '~src/flow/resources/Flows';
import { RevertFlowResult } from '~src/flow/serverApi/requests/RevertFlowRequest';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { ResultHelper } from '~src/httpClient/utils/ResultHelper';
import { FlowRevision } from '~src/revisionHistoryFlow/serverApi/types/FlowRevision';
import { IWorkbookRevision } from '~src/revisionHistoryWorkbook/serverApi/types/IWorkbookRevision';
import { DatasourceContentType, FlowContentType, IContentType, PublishedConnectionContentType, WorkbookContentType } from '~src/spacesPlatform/models/ContentTypes';
import { L10n } from '~src/ts/L10n/L10n';
import { INamedItemInfo } from '~src/ts/models/resourceInfoParts';
import { DownloadActions } from '~src/ts/services/download/DownloadActions';
import { ToasterService, ToastType } from '~src/ts/services/ToasterService';
import { Workbooks } from '~src/workbook/resources/Workbooks';
import { IRevertWorkbookResult } from '~src/workbook/ServerApi/requests/RevertWorkbookRequest';

import {
  RevisionHistoryDialogBody,
  RevisionHistoryDialogBodyProps,
  RevisionHistoryDialogBodyState
} from '../components/RevisionHistoryDialogBody';


class RevisionHistoryAction {

  private showContentRevisionHistory(content: INamedItemInfo, contentType: IContentType): Promise<IResult> {

    let dialogDescription = '';
    let action; // 10.0 dialog action for workbooks should be one-click revert. Otherwise it's download revision.
    let actionName: string;
    const elidedContentName = {NAME: L10n.elideIfLong(content.name)};
    switch (contentType.name) {
      case WorkbookContentType.name:
        action = this.getRevertWorkbookAction;
        actionName = Messages.revisionHistoryDialogSubmitRestore();
        dialogDescription = Messages.revisionHistoryDialogWorkbookDescription(elidedContentName);
        break;
      case DatasourceContentType.name:
        action = this.getDownloadRevisionAction;
        actionName = Messages.revisionHistoryDialogSubmitDownload();
        dialogDescription = Messages.revisionHistoryDialogDatasourceDescription(elidedContentName);
        break;
      case FlowContentType.name:
        action = this.getRevertFlowAction;
        actionName = Messages.revisionHistoryDialogSubmitRestore();
        dialogDescription = Messages.revisionHistoryDialogFlowDescription(elidedContentName);
        break;
      case PublishedConnectionContentType.name:
        // TODO: whudson, tfsid-1278354, 7/14/21, write the action for opening the revision as an IFR (once we get data)
        action = this.getDownloadRevisionAction;
        actionName = Messages.revisionHistoryDialogSubmitOpen();
        dialogDescription = Messages.revisionHistoryDialogPublishedConnectionDescription(elidedContentName);
        break;
      default:
        throw new Error(Messages.revisionHistoryUnsupportedContentType());
    }
    const revisionHistoryDialogOptions: ReactActionDialogOptions<RevisionHistoryDialogBodyProps, RevisionHistoryDialogBodyState, IResult> = {
        title: Messages.revisionHistoryDialogTitle(),
        initialBodyProps: {contentType, dialogDescription, contentItemId: content.id},
        initialDialogState: {selectedItem: null, deletedItem: null},
        action: action.bind(this),
        bodyComponent: RevisionHistoryDialogBody,
        dismissDialogOnActionFailed: false,
        actionName: actionName,
        actionDisabled: this.isRestoreButtonDisabled,
        dialogWidth: TabDialogWidth.width600px,
        isActionDelete: false
    };
    const instance = openReactActionDialog(revisionHistoryDialogOptions);
    return Promise.resolve(instance.result);
  }

  private getRevertWorkbookAction(props: RevisionHistoryDialogBodyProps,
                                  state: RevisionHistoryDialogBodyState,
                                  updateState: React.Dispatch<MergeOrUpdateStateAction<RevisionHistoryDialogBodyState>>): PromiseLike<IResult> {
      const revision: IWorkbookRevision = <IWorkbookRevision>state.selectedItem;
      return Workbooks.revertWorkbook(props.contentItemId, revision.versionNumber)
        .then((result: IRevertWorkbookResult): Promise<IRevertWorkbookResult> => {
            if (result.failedConnection) {
              const checkCredentialsDialogs = CheckConnectionCredentialsAction.showCheckCredentialsDialogs(result.failedConnection, revision);
              return Promise.resolve(checkCredentialsDialogs);
            } else {
              return Promise.resolve(result);
            }
          })
        .then((result: IRevertWorkbookResult) => {
            if (ResultHelper.hasErrors(result)) {
              updateState((prev) => ({...prev, errorMessage: Messages.revisionHistoryActionErrorForWorkbook()}));
              return Promise.reject(result);
            } else {
              ToasterService.instance.pop({ type: ToastType.Info, text: Messages.revisionHistoryActionSuccessForWorkbook() });
              if (result.scheduledExtractCredentialsMayNotBeRestored) {
                ToasterService.instance.pop({ type: ToastType.Info, text: Messages.revisionHistoryActionSuccessGoToConnections() });
              }
              return Promise.resolve(result);
            }
          });
  }

  private getDownloadRevisionAction(props: RevisionHistoryDialogBodyProps,
                                    state: RevisionHistoryDialogBodyState,
                                    updateState: React.Dispatch<MergeOrUpdateStateAction<RevisionHistoryDialogBodyState>>): PromiseLike<IResult> {
      DownloadActions.downloadContentRevision(state.selectedItem);
      return Promise.resolve({} as any);
  }

  private getRevertFlowAction(props: RevisionHistoryDialogBodyProps,
                              state: RevisionHistoryDialogBodyState,
                              updateState: React.Dispatch<MergeOrUpdateStateAction<RevisionHistoryDialogBodyState>>): PromiseLike<IResult> {
      const revision: FlowRevision = <FlowRevision>state.selectedItem;
      return Flows.revertFlow(props.contentItemId, revision.versionNumber)
        .then((result: RevertFlowResult): Promise<RevertFlowResult> => {
          if (result.failedConnection) {
            return Promise.resolve(CheckConnectionCredentialsAction.showCheckCredentialsDialogs(result.failedConnection, revision));
          } else {
            return Promise.resolve(result);
          }
        })
        .then((result: RevertFlowResult) => {
          if (ResultHelper.hasErrors(result)) {
            updateState((prev) => ({...prev, errorMessage: Messages.revisionHistoryActionErrorForFlow()}));
            return Promise.reject(result);
          } else {
            ToasterService.instance.pop({ type: ToastType.Info, text: Messages.revisionHistoryActionSuccessForFlow() });
            return Promise.resolve(result);
          }
        });
  }

  public showDataSourceRevisionHistory(datasource: INamedItemInfo): Promise<IResult> {
    return this.showContentRevisionHistory(datasource, DatasourceContentType);
  }

  public showWorkbookRevisionHistory(workbook: INamedItemInfo): Promise<IResult> {
    return this.showContentRevisionHistory(workbook, WorkbookContentType);
  }

  public showFlowRevisionHistory(flow: INamedItemInfo): Promise<IResult> {
    return this.showContentRevisionHistory(flow, FlowContentType);
  }

  public showPublishedConnectionRevisionHistory(publishedConnection: INamedItemInfo): Promise<IResult> {
    return this.showContentRevisionHistory(publishedConnection, PublishedConnectionContentType);
  }

  private isRestoreButtonDisabled(props: RevisionHistoryDialogBodyProps, state: RevisionHistoryDialogBodyState) {
    return state.selectedItem === null;
  }
}

export const RevisionHistoryActionInstance = new RevisionHistoryAction();
export type {RevisionHistoryAction};
